import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import Slider from 'react-slick';
import clsx from 'clsx';
import {
    Box,
    Typography,
    Grid,
    makeStyles,
    List,
    ListItem,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@material-ui/core';
import CarouselArrow from '../../components/UI/CarouselArrow';
import SettingsIcon from '@material-ui/icons/Settings';
import BusinessCenterIcon from '@material-ui/icons/BusinessCenter';

import { useStores } from '../../hooks/use-stores';
import Loader from '../../components/Loader';
import PointsValue from '../../components/UI/PointsValue';
import RentOrderModal from '../../components/Order/RentOrderModal';
import BuyOrderModal from '../../components/Order/BuyOrderModal';
import CartAddedModal from '../../components/Cart/CartAddedModal';
import NotificationModal from '../../components/NotificationModal';
import BackLink from '../../components/UI/BackLink';

const Product = observer(() => {
    const classes = useStyles();
    const { categoryId } = useParams();
    const { ProductStore: store, HeaderStore, CartStore, ProductStore, DiscountStore, OrderStore } = useStores();

    const [slider1, setSlider1] = useState(null);
    const [slider2, setSlider2] = useState(null);

    const [openRentModal, setOpenRentModal] = React.useState(false);
    const [openBuyModal, setOpenBuyModal] = React.useState(false);

    const { productId } = useParams();

    const sliderSettings = {
        dots: false,
        arrows: false,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        asNavFor: slider2,
    };

    const navSliderSettings = {
        infinite: false,
        slidesToShow: store.product?.images.length < 3 ? store.product?.images.length - 1 : 3,
        slidesToScroll: 1,
        asNavFor: slider1,
        focusOnSelect: true,
        prevArrow: <CarouselArrow type="prev" />,
        nextArrow: <CarouselArrow type="next" />,
    };

    // Effects
    useEffect(() => {
        store.getProduct(productId);

        return () => {
            OrderStore.setProduct({});
            CartStore.setAddedModal(false);
        };
    }, [store, productId, OrderStore, CartStore]);

    useEffect(() => {
        if (store.product) {
            HeaderStore.setTitle(store.product.name);
        }
    }, [HeaderStore, store.product]);

    // add product to order
    useEffect(() => {
        if (store.product) {
            const { id, images, name, price, priceStr, properties, cartPropsList } = store.product;

            OrderStore.setProduct({
                id: id,
                link: `/catalog/${categoryId}/${id}`,
                img: images[0],
                name,
                price: price || priceStr,
                points: properties?.bonuses_count?.value,
                properties:
                    cartPropsList?.length &&
                    cartPropsList.map(propName => {
                        const prop = properties[propName];
                        if (prop) {
                            return {
                                name: prop?.name,
                                value: `${prop?.value} ${prop?.hint}`,
                            };
                        }
                        return null;
                    }),
            });
        }
    }, [DiscountStore.userLevelInfo, ProductStore.product, OrderStore, store.product, categoryId]);

    // Renders
    if (store.loading) {
        return <Loader />;
    }

    if (!store.loading && store.product) {
        const { images, points, tech, priceStr, properties } = store.product;

        return (
            <Box className={classes.root}>
                <BackLink to={`/catalog/${categoryId}`} title="К категории" />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <Box>
                            <Slider {...sliderSettings} ref={slider => setSlider1(slider)} className={classes.slider}>
                                {images.map((img, index) => (
                                    <img key={index} src={img} alt="" />
                                ))}
                            </Slider>

                            <Slider {...navSliderSettings} rref={slider => setSlider2(slider)} className={classes.navSlider}>
                                {images.map((img, index) => (
                                    <img key={index} src={img} alt="" />
                                ))}
                            </Slider>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Box mb={7}>
                            <List className={classes.propsList}>
                                {tech.map((prop, index) => (
                                    <PropertyItem key={index} label={prop.description}>
                                        {prop.value}
                                    </PropertyItem>
                                ))}
                            </List>
                        </Box>

                        {store.product?.hasRent && (
                            <Box
                                display="flex"
                                alignItems={{ xxs: 'flex-start', sm: 'center' }}
                                flexDirection={{ xxs: 'column', sm: 'row' }}
                                mb={3}
                            >
                                <Typography>Стоимость аренды:</Typography>
                                <Box display="flex" alignItems="center">
                                    <Box ml={{ xxs: 0, sm: 2 }} mr={2}>
                                        <Typography variant="h6" style={{ fontWeight: '700' }}>
                                            {priceStr}
                                        </Typography>
                                    </Box>
                                    {points && <PointsValue size="large">{points}</PointsValue>}
                                </Box>
                            </Box>
                        )}

                        <Box display="flex" alignItems="center" mb={4}>
                            {store.product?.hasRent && (
                                <Box mr={2}>
                                    <Button variant="outlined" color="primary" onClick={() => setOpenRentModal(true)}>
                                        Арендовать
                                    </Button>
                                </Box>
                            )}
                            {store.product?.hasBuy && (
                                <Button variant="outlined" color="primary" onClick={() => setOpenBuyModal(true)}>
                                    Купить
                                </Button>
                            )}
                            {!store.product?.hasRent && store.product?.hasBuy && points && (
                                <Box ml={2}>
                                    <PointsValue size="large">{points}</PointsValue>
                                </Box>
                            )}
                        </Box>

                        <Box>
                            {properties?.dop && properties?.dop?.value && (
                                <Accordion elevation={0} className={classes.accordion}>
                                    <AccordionSummary className={classes.accordionHeader}>
                                        <Box display="flex" alignItems="center">
                                            <Box display="flex" mr={1} color="#777">
                                                <SettingsIcon fontSize="small" />
                                            </Box>
                                            <Typography variant="body2" color="primary">
                                                Смотреть базовую комплектацию
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionContent}>
                                        <List dense>
                                            {properties.dop?.value.map((item, index) => (
                                                <ListItem alignItems="flex-start" key={index}>
                                                    <Box mr={2}>
                                                        <Typography color="textSecondary">•</Typography>
                                                    </Box>
                                                    <Typography color="textSecondary">{item}</Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            )}

                            {properties?.dop_complectation && properties?.dop_complectation?.value && (
                                <Accordion elevation={0} className={classes.accordion}>
                                    <AccordionSummary className={classes.accordionHeader}>
                                        <Box display="flex" alignItems="center">
                                            <Box display="flex" mr={1} color="#777">
                                                <BusinessCenterIcon fontSize="small" />
                                            </Box>
                                            <Typography variant="body2" color="primary">
                                                Смотреть дополнительное оборудование
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails className={classes.accordionContent}>
                                        <List dense>
                                            {properties.dop_complectation?.value.map((item, index) => (
                                                <ListItem alignItems="flex-start" key={index}>
                                                    <Box mr={2}>
                                                        <Typography color="textSecondary">•</Typography>
                                                    </Box>
                                                    <Typography color="textSecondary">{item}</Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                <RentOrderModal open={openRentModal} onClose={() => setOpenRentModal(false)} />
                <BuyOrderModal open={openBuyModal} onClose={() => setOpenBuyModal(false)} />
                <CartAddedModal open={CartStore.addedModal} onClose={() => CartStore.setAddedModal(false)} />
                <NotificationModal
                    title="Ваша заявка успешно отправлена!"
                    message="В течение 15 мин с Вами свяжется менеджер для уточнения деталей заказа"
                    open={OrderStore.success}
                    withoutCloseBtn
                />
            </Box>
        );
    }

    return <div />;
});

const PropertyItem = ({ label, children }) => {
    const classes = useStyles();

    return (
        <ListItem className={classes.propsItem}>
            <Typography variant="body2" className={clsx(classes.propsItemCol, classes.propsItemColLeft)}>
                {label}
            </Typography>
            <Typography variant="body2" className={clsx(classes.propsItemCol, classes.propsItemColRight)}>
                {children}
            </Typography>
        </ListItem>
    );
};

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        padding: theme.spacing(3, 2, 3),
        marginBottom: theme.spacing(10),
        boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.1)',
    },
    slider: {
        maxWidth: 353,
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('md')]: {
            maxWidth: 295,
        },
        [theme.breakpoints.down('sm')]: {
            maxWidth: 400,
            margin: theme.spacing(0, 'auto', 3),
        },
        '& .slick-slide': {
            cursor: 'pointer',
            padding: theme.spacing(0.7),
            '& div': {
                width: 329,
                height: 329,
                margin: '0 auto',
                [theme.breakpoints.down('xs')]: {
                    width: 290,
                    height: 290,
                },
                '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                },
            },
        },
    },
    navSlider: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: 353,
        [theme.breakpoints.down('md')]: {
            maxWidth: 295,
        },
        [theme.breakpoints.down('sm')]: {
            maxWidth: 400,
            margin: theme.spacing(0, 'auto', 3),
        },
        '& .slick-slide': {
            cursor: 'pointer',
            padding: theme.spacing(0.7),
            '& div': {
                width: '100%',
                height: 80,
                padding: theme.spacing(1),
                border: '1px solid #eee',
                [theme.breakpoints.down('xs')]: {
                    padding: theme.spacing(0.5),
                },
                '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                },
            },

            '&.slick-current': {
                '& div': {
                    borderColor: theme.palette.primary.main,
                },
            },
        },
    },
    preview: {},
    propsList: {
        padding: 0,
    },
    propsItem: {
        padding: 0,
        alignItems: 'stretch',
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    propsItemCol: {
        padding: theme.spacing(1),
        margin: theme.spacing(0, 0.5),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(0.3),
            margin: theme.spacing(0, 0.2),
        },
    },
    propsItemColLeft: {
        flex: 1,
        backgroundColor: '#f4f4f4',
        [theme.breakpoints.down('sm')]: {
            flex: 1,
            fontSize: 12,
            backgroundColor: 'transparent',
        },
    },
    propsItemColRight: {
        flex: 1.5,
        backgroundColor: '#fcfcfc',
        [theme.breakpoints.down('sm')]: {
            flex: 1,
            // fontSize: 14,
            color: '#222',
            backgroundColor: 'transparent',
        },
    },
    accordion: {
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            margin: '0 !important',
        },
    },
    accordionHeader: {
        padding: 0,
        '& .MuiTypography-root': {
            borderBottom: '1px dashed #a0a0a0',
        },
        '&.Mui-expanded': {
            minHeight: 48,
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: theme.spacing(1, 0),
        },
    },
    accordionContent: {
        padding: theme.spacing(0, 0, 0, 1.4),
    },
}));

export default Product;
